import React, { useMemo, useRef, useState } from "react";
import { useTheme, THEME_PRESETS } from "./ThemeProvider";

type Hsl = { h: number; s: number; l: number };
const parseHsl = (s: string): Hsl => {
  const inner = s.includes("hsl(") ? s.slice(s.indexOf("(")+1, s.lastIndexOf(")")) : s;
  const [H,S,L] = inner.replace(/,/g," ").trim().split(/\s+/);
  return { h: +H||0, s: +(S||"0").replace("%",""), l: +(L||"0").replace("%","") };
};
const fmt = ({h,s,l}:Hsl) => `hsl(${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%)`;
const fmtComma = ({h,s,l}:Hsl) => `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;

const TOKENS: { k: string; label: string; kind: "hsl" | "px" }[] = [
  { k: "--dashboard-bg", label: "Background", kind: "hsl" },
  { k: "--dashboard-surface", label: "Surface", kind: "hsl" },
  { k: "--dashboard-surface-2", label: "Surface+", kind: "hsl" },
  { k: "--dashboard-surface-3", label: "Surface++", kind: "hsl" },
  { k: "--dashboard-text", label: "Text", kind: "hsl" },
  { k: "--dashboard-text-muted", label: "Text Muted", kind: "hsl" },

  { k: "--dashboard-primary", label: "Primary", kind: "hsl" },
  { k: "--dashboard-primary-fg", label: "Primary Text", kind: "hsl" },
  { k: "--dashboard-accent", label: "Accent", kind: "hsl" },
  { k: "--dashboard-accent-fg", label: "Accent Text", kind: "hsl" },

  { k: "--dashboard-border", label: "Border", kind: "hsl" },
  { k: "--dashboard-input-bg", label: "Input BG", kind: "hsl" },

  /* Chips */
  { k: "--dashboard-chip-bg", label: "Chip BG", kind: "hsl" },
  { k: "--dashboard-chip-fg", label: "Chip Text", kind: "hsl" },
  { k: "--dashboard-chip-border", label: "Chip Border", kind: "hsl" },

  /* Shadows color + radii */
  { k: "--dashboard-shadow", label: "Shadow Color", kind: "hsl" },
  { k: "--dashboard-radius-sm", label: "Radius Sm", kind: "px" },
  { k: "--dashboard-radius", label: "Radius", kind: "px" },
  { k: "--dashboard-radius-lg", label: "Radius Lg", kind: "px" },
];

function HslGroup({ k, label, value, onChange }:{
  k:string; label:string; value:string; onChange:(v:string)=>void;
}) {
  const hsl = useMemo(()=>parseHsl(value),[value]);
  const {h,s,l} = hsl;
  const swatch = fmt(hsl);
  return (
    <div className="group">
      <div className="head">
        <strong>{label}</strong>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div className="swatch" style={{ background: swatch }} title={`${k}: ${fmtComma(hsl)}`} />
          <code className="code">{fmtComma(hsl)}</code>
        </div>
      </div>
      <div className="row"><span>H</span><input type="range" min={0} max={360} value={h} onChange={e=>onChange(fmt({h:+e.target.value,s,l}))}/><span>{Math.round(h)}</span></div>
      <div className="row"><span>S</span><input type="range" min={0} max={100} value={s} onChange={e=>onChange(fmt({h,s:+e.target.value,l}))}/><span>{Math.round(s)}%</span></div>
      <div className="row"><span>L</span><input type="range" min={0} max={100} value={l} onChange={e=>onChange(fmt({h,s,l:+e.target.value}))}/><span>{Math.round(l)}%</span></div>
      <small className="opacity-70">{k}: {swatch}</small>
    </div>
  );
}

function PxGroup({ k, label, value, onChange }:{
  k:string; label:string; value:string; onChange:(v:string)=>void;
}) {
  const n = parseInt(value,10)||0;
  return (
    <div className="group">
      <div className="head"><strong>{label}</strong><span className="code">{value}</span></div>
      <div className="row" style={{gridTemplateColumns:"auto 1fr 60px"}}>
        <span>px</span>
        <input type="range" min={6} max={32} value={n} onChange={e=>onChange(`${e.target.value}px`)} />
        <input className="input" value={`${n}px`} onChange={e=>onChange(e.target.value)} />
      </div>
    </div>
  );
}

export default function ThemePanel() {
  const { themeName, vars, applyTheme, resetToPreset, exportJSON, importJSONText,
          savedNames, saveAs, loadSaved, deleteSaved, setFont, currentFont } = useTheme();
  const [open, setOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const setVar = (k:string, v:string) => applyTheme({ ...vars, [k]: v });

  const handleExportFile = () => {
    const blob = new Blob([exportJSON()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `theme-${themeName}.json`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const handleImportFile = async (f: File) => importJSONText(await f.text());

  return (
    <>
      <button onClick={()=>setOpen(o=>!o)} className="btn btn--primary fixed bottom-4 right-4 z-50" style={{ borderRadius: 9999 }}>
        🎨 Theme
      </button>

      {open && (
        <div className="admin-dashboard panel theme-panel fixed bottom-16 right-4 z-50 w-[380px] max-h-[82vh] overflow-auto p-3">
          <div className="head" style={{marginBottom:12}}>
            <h3 className="text-base font-semibold">Theme</h3>
            <button className="btn btn--outline" onClick={()=>setOpen(false)}>✕</button>
          </div>

          {/* Font picker (Google Fonts) */}
          <div className="group" style={{ marginBottom: 12 }}>
            <div className="head"><strong>Font</strong><span className="code">{currentFont}</span></div>
            <div className="row" style={{gridTemplateColumns:"auto 1fr auto"}}>
              <span>Sans</span>
              <select className="input" defaultValue={currentFont} onChange={e=>setFont(e.target.value)}>
                <option value="Inter:300..700">Inter</option>
                <option value="Rubik:300..900">Rubik</option>
                <option value="Poppins:300..800">Poppins</option>
                <option value="Nunito:300..800">Nunito</option>
                <option value="Montserrat:300..800">Montserrat</option>
                <option value="IBM Plex Sans:300..700">IBM Plex Sans</option>
              </select>
              <span />
            </div>
          </div>

          {/* Presets / Save / Import-Export */}
          <div className="group" style={{ marginBottom: 12 }}>
            <div className="head"><strong>Presets & Save</strong></div>
            <div className="row" style={{gridTemplateColumns:"auto 1fr auto"}}>
              <span>Preset</span>
              <select className="input" defaultValue={themeName} onChange={e=>resetToPreset(e.target.value)}>
                {(Object.keys(THEME_PRESETS) as string[]).map(n=>(<option key={n} value={n}>{n}</option>))}
              </select>
              <button className="btn btn--outline" onClick={()=>resetToPreset("Dark")}>Reset</button>
            </div>
            <div className="row" style={{gridTemplateColumns:"auto 1fr auto"}}>
              <span>Save</span>
              <input id="save-name" className="input" placeholder="Save as…" />
              <button className="btn btn--outline" onClick={()=>{
                const el = document.getElementById("save-name") as HTMLInputElement;
                const name = (el?.value || "").trim(); if (name) { saveAs(name); el.value=""; }
              }}>Save</button>
            </div>
            {savedNames.length>0 && (
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8}}>
                {savedNames.map(n=>(
                  <span key={n} className="chip">
                    {n}
                    <button className="btn btn--outline" style={{padding:"2px 6px"}} onClick={()=>loadSaved(n)}>Load</button>
                    <button className="btn btn--outline" style={{padding:"2px 6px"}} onClick={()=>deleteSaved(n)}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <div className="row" style={{gridTemplateColumns:"auto auto 1fr"}}>
              <button className="btn btn--outline" onClick={handleExportFile}>Export .json</button>
              <button className="btn btn--outline" onClick={()=>fileRef.current?.click()}>Import .json</button>
              <input ref={fileRef} type="file" accept="application/json" hidden onChange={e=>{ const f=e.target.files?.[0]; if (f) handleImportFile(f); }} />
            </div>
          </div>

          {/* All color/radius controls (boxed previews) */}
          <div className="space-y-3">
            {TOKENS.map(({k,label,kind})=>{
              const val = vars[k] ?? (kind==="px" ? "12px" : "hsl(210 40% 98%)");
              return kind==="hsl"
                ? <HslGroup key={k} k={k} label={label} value={val} onChange={nv=>applyTheme({ ...vars, [k]: nv })} />
                : <PxGroup key={k} k={k} label={label} value={val} onChange={nv=>applyTheme({ ...vars, [k]: nv })} />;
            })}
          </div>

          {/* Shadow alpha sliders */}
          <div className="group" style={{ marginTop: 12 }}>
            <div className="head"><strong>Shadow Alphas</strong></div>
            {(["--dashboard-shadow-1a","--dashboard-shadow-2a","--dashboard-shadow-3a"] as const).map((k,i)=>{
              const val = Number(vars[k] ?? (i===0?0.25:i===1?0.30:0.35));
              return (
                <div key={k} className="row" style={{gridTemplateColumns:"40px 1fr 60px"}}>
                  <span>α{i+1}</span>
                  <input type="range" min={0} max={1} step={0.01} value={val}
                    onChange={e=>applyTheme({ ...vars, [k]: String(Math.max(0, Math.min(1, Number(e.target.value)))) })} />
                  <input className="input" value={val.toFixed(2)}
                         onChange={e=>applyTheme({ ...vars, [k]: String(Math.max(0, Math.min(1, Number(e.target.value)))) })}/>
                </div>
              );
            })}
          </div>

          {/* Tiny live preview */}
          <div className="card" style={{ padding: 12, marginTop: 12 }}>
            <div style={{ display:"flex", gap: 10, flexWrap:"wrap" }}>
              <span className="chip">Neutral</span>
              <span className="chip">Active</span>
              <span className="chip">Beta</span>
              <span className="chip">Marketing</span>
              <button className="btn btn--primary">Primary</button>
              <input className="input" placeholder="Input…" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
